import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

interface AuthenticatedRedirectProps {
  children: React.ReactNode;
}

/**
 * Wrapper that redirects authenticated users to /dashboard
 * Use this on the homepage to make dashboard the default for logged-in users
 * Exception: If URL has a hash (e.g., /#live-games), show the homepage section instead
 */
export function AuthenticatedRedirect({ children }: AuthenticatedRedirectProps) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect if there's a hash - user wants to see a specific section
    if (!isLoading && user && !location.hash) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, isLoading, navigate, location.hash]);

  // Show nothing while checking auth to avoid flash
  if (isLoading) {
    return null;
  }

  // If user is authenticated but has a hash, show the content (don't redirect)
  if (user && location.hash) {
    return <>{children}</>;
  }

  // If user is authenticated without hash, they'll be redirected, so show nothing
  if (user) {
    return null;
  }

  // Show the wrapped content for non-authenticated users
  return <>{children}</>;
}

export default AuthenticatedRedirect;
